// ─────────────────────────────────────────
// ChatPage.js — chat hub
// ─────────────────────────────────────────
// Desktop: sidebar (DMs + groups) + main chat panel — unchanged
// Mobile:  Slack-style two-screen flow:
//   Screen 1 — full-screen conversation list (logo, search, DMs, Groups)
//   Screen 2 — full-screen chat with a ← back button, no bottom nav

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'
import GroupChat from '../components/GroupChat'
import DirectChat from '../components/DirectChat'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function ChatPage({ session }) {
  const isMobile = useIsMobile()
  const [searchParams] = useSearchParams()

  const [myGroups, setMyGroups] = useState([])
  const [myProfile, setMyProfile] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedDM, setSelectedDM] = useState(null)
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Mobile-only: which screen we're on
  const [mobileView, setMobileView] = useState('list') // 'list' | 'chat'
  const [mobileSearch, setMobileSearch] = useState('')

  // Following list for DMs
  const [following, setFollowing] = useState([])
  const [dmSearch, setDmSearch] = useState('')

  useEffect(() => {
    fetchMyProfile()
    fetchMyGroups()
    fetchFollowing()
  }, [])

  // Handle ?dm=userId deep-link
  useEffect(() => {
    const dmUserId = searchParams.get('dm')
    if (!dmUserId || following.length === 0) return
    const match = following.find(u => u.id === dmUserId)
    if (match) {
      setSelectedDM(match); setSelectedGroup(null)
      if (isMobile) setMobileView('chat')
    } else {
      supabase.from('profiles')
        .select('id, username, full_name, avatar_emoji, avatar_url')
        .eq('id', dmUserId).single()
        .then(({ data }) => {
          if (data) {
            setSelectedDM(data); setSelectedGroup(null)
            if (isMobile) setMobileView('chat')
          }
        })
    }
  }, [following, searchParams])

  async function fetchMyProfile() {
    const { data } = await supabase
      .from('profiles').select('id, username, avatar_emoji, avatar_url')
      .eq('id', session.user.id).single()
    if (data) setMyProfile(data)
  }

  async function fetchMyGroups() {
    // Flat separate queries — embedded join silently drops private groups if RLS blocks them
    const { data: memberRows } = await supabase
      .from('group_members').select('group_id, role')
      .eq('user_id', session.user.id)

    const roleMap = {}
    ;(memberRows || []).forEach(r => { roleMap[r.group_id] = r.role })
    const groupIds = Object.keys(roleMap)

    let groups = []
    if (groupIds.length > 0) {
      const { data: groupRows } = await supabase
        .from('groups').select('*').in('id', groupIds)
      groups = (groupRows || []).map(g => ({ ...g, myRole: roleMap[g.id] }))
    }

    setMyGroups(groups)
    setLoadingGroups(false)
    if (groups.length > 0 && !selectedGroup && !selectedDM && !searchParams.get('dm') && !isMobile) {
      setSelectedGroup(groups[0])
    }
  }

  async function fetchFollowing() {
    const { data: rows } = await supabase
      .from('follows')
      .select('profiles!follows_following_id_fkey(id, username, full_name, avatar_emoji, avatar_url)')
      .eq('follower_id', session.user.id)
    const people = (rows || []).map(r => r.profiles).filter(Boolean)
    setFollowing(people)
  }

  function selectDM(user) {
    setSelectedDM(user); setSelectedGroup(null)
    if (isMobile) { setMobileView('chat') }
    else setSidebarOpen(true)
  }

  function selectGroup(g) {
    setSelectedGroup(g); setSelectedDM(null)
    if (isMobile) { setMobileView('chat') }
    else if (isMobile) setSidebarOpen(false)
  }

  function getLastSeen(groupId) {
    try { return localStorage.getItem(`chat_seen_${groupId}`) || null } catch { return null }
  }
  function markSeen(groupId, msgId) {
    try { localStorage.setItem(`chat_seen_${groupId}`, msgId) } catch {}
  }

  // Desktop DM search filter
  const dmResults = dmSearch.trim()
    ? following.filter(u =>
        (u.username || '').toLowerCase().includes(dmSearch.toLowerCase()) ||
        (u.full_name || '').toLowerCase().includes(dmSearch.toLowerCase()))
    : following

  // Mobile search filters both DMs + groups
  const q = mobileSearch.toLowerCase().trim()
  const filteredDMs = q
    ? following.filter(u =>
        (u.username || '').toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q))
    : following
  const filteredGroups = q
    ? myGroups.filter(g => (g.name || '').toLowerCase().includes(q))
    : myGroups

  // ─────────────────────────────────────────
  // MOBILE — Screen 1: conversation list
  // ─────────────────────────────────────────
  if (isMobile && mobileView === 'list') {
    return (
      <div style={mob.page}>

        {/* Header */}
        <div style={mob.header}>
          <div style={mob.headerTop}>
            <div style={mob.logo}>LOOPER</div>
            <div style={mob.meAvatar}><AvatarImg profile={myProfile} /></div>
          </div>
          {/* Search */}
          <div style={mob.searchWrap}>
            <span style={mob.searchIcon}>🔍</span>
            <input
              style={mob.searchInput}
              placeholder="Jump to or search..."
              value={mobileSearch}
              onChange={e => setMobileSearch(e.target.value)}
            />
            {mobileSearch.length > 0 && (
              <button style={mob.searchClear} onClick={() => setMobileSearch('')}>✕</button>
            )}
          </div>
        </div>

        <Nav />

        {/* List body */}
        <div style={mob.body}>

          {/* ── Direct Messages ── */}
          <div style={mob.sectionLabel}>Direct Messages</div>

          {following.length === 0 && (
            <div style={mob.emptySection}>
              Follow golfers to message them
            </div>
          )}
          {q && filteredDMs.length === 0 && following.length > 0 && (
            <div style={mob.emptySection}>No people matching "{mobileSearch}"</div>
          )}
          {filteredDMs.map(user => (
            <button key={user.id} style={mob.row} onClick={() => selectDM(user)}>
              <div style={mob.dmAvatar}><AvatarImg profile={user} /></div>
              <div style={mob.rowInfo}>
                <div style={mob.rowName}>{user.full_name || user.username || 'Golfer'}</div>
                {user.username && <div style={mob.rowSub}>@{user.username}</div>}
              </div>
              <div style={mob.rowChevron}>›</div>
            </button>
          ))}

          {/* ── Groups ── */}
          <div style={{ ...mob.sectionLabel, marginTop: 8 }}>
            Groups
            <Link to="/groups/create" style={mob.sectionAdd}>＋ New</Link>
          </div>

          {loadingGroups && <div style={mob.emptySection}>Loading...</div>}
          {!loadingGroups && myGroups.length === 0 && (
            <div style={mob.emptySection}>
              No groups yet.{' '}
              <Link to="/groups" style={{ color: '#1a7a3c', fontWeight: 700 }}>Browse →</Link>
            </div>
          )}
          {q && filteredGroups.length === 0 && myGroups.length > 0 && (
            <div style={mob.emptySection}>No groups matching "{mobileSearch}"</div>
          )}
          {filteredGroups.map(g => (
            <button key={g.id} style={mob.row} onClick={() => selectGroup(g)}>
              <div style={mob.groupIcon}>{g.icon_emoji || '⛳'}</div>
              <div style={mob.rowInfo}>
                <div style={mob.rowName}>{g.name}</div>
                {g.myRole === 'admin' && <div style={{ ...mob.rowSub, color: '#1a7a3c', fontWeight: 700 }}>Admin</div>}
              </div>
              <div style={mob.rowChevron}>›</div>
            </button>
          ))}

          <div style={{ height: 16 }} />
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────
  // MOBILE — Screen 2: full-screen chat
  // ─────────────────────────────────────────
  if (isMobile && mobileView === 'chat') {
    const isGroup = !!selectedGroup && !selectedDM
    const chatName = selectedDM
      ? (selectedDM.full_name || `@${selectedDM.username}`)
      : (selectedGroup?.name || 'Chat')

    return (
      <div style={mob.chatPage}>

        {/* Chat header */}
        <div style={mob.chatHeader}>
          <button style={mob.backBtn} onClick={() => setMobileView('list')}>
            ‹
          </button>
          <div style={isGroup ? mob.chatHeaderGroupIcon : mob.chatHeaderDMAvatar}>
            {selectedDM
              ? <AvatarImg profile={selectedDM} />
              : (selectedGroup?.icon_emoji || '⛳')}
          </div>
          <div style={mob.chatHeaderInfo}>
            <div style={mob.chatHeaderName}>{chatName}</div>
            {selectedDM?.username && (
              <div style={mob.chatHeaderSub}>@{selectedDM.username}</div>
            )}
          </div>
          {selectedDM && (
            <Link to={`/passport/${selectedDM.id}`} style={mob.viewProfileBtn}>
              Profile
            </Link>
          )}
        </div>

        {/* Chat body */}
        <div style={mob.chatBody}>
          {selectedDM && (
            <DirectChat
              key={selectedDM.id}
              otherUser={selectedDM}
              session={session}
              myProfile={myProfile}
            />
          )}
          {selectedGroup && !selectedDM && (
            <GroupChat
              key={selectedGroup.id}
              group={selectedGroup}
              session={session}
              myProfile={myProfile}
              isMember={true}
              markSeen={markSeen}
              fullHeight
            />
          )}
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────
  // DESKTOP — sidebar + main panel (unchanged)
  // ─────────────────────────────────────────
  return (
    <div style={s.page}>

      {/* ── TOP BAR ── */}
      <div style={s.topbar}>
        <div style={s.logo}>LOOPER</div>
        <div style={s.topbarRight}>
          <div style={s.meAvatar}><AvatarImg profile={myProfile} /></div>
          <div style={s.meName}>@{myProfile?.username || '...'}</div>
        </div>
      </div>

      <Nav />

      {/* ── Body: sidebar + chat ── */}
      <div style={s.body}>

        {/* ── Left sidebar ── */}
        <div style={{ ...s.sidebar, ...(sidebarOpen ? {} : s.sidebarHidden) }}>

          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Direct Messages</div>
          </div>

          <div style={s.dmSearchWrap}>
            <input
              style={s.dmSearchInput}
              placeholder="Search people you follow..."
              value={dmSearch}
              onChange={e => setDmSearch(e.target.value)}
            />
          </div>

          <div style={s.dmList}>
            {following.length === 0 && (
              <div style={s.sidebarEmpty}>Follow golfers to message them</div>
            )}
            {following.length > 0 && dmResults.length === 0 && dmSearch.trim() && (
              <div style={s.sidebarEmpty}>No results for "{dmSearch}"</div>
            )}
            {dmResults.map(user => (
              <button key={user.id}
                style={{ ...s.dmBtn, ...(selectedDM?.id === user.id ? s.dmBtnActive : {}) }}
                onClick={() => selectDM(user)}>
                <div style={s.dmBtnAvatar}><AvatarImg profile={user} /></div>
                <div style={s.dmBtnInfo}>
                  <div style={s.dmBtnName}>{user.full_name || user.username || 'Golfer'}</div>
                  {user.username && <div style={s.dmBtnHandle}>@{user.username}</div>}
                </div>
                {selectedDM?.id === user.id && <div style={s.activeIndicator} />}
              </button>
            ))}
          </div>

          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Groups</div>
            <Link to="/groups/create" style={s.sidebarCreateBtn}>＋</Link>
          </div>

          <div style={s.groupList}>
            {loadingGroups && <div style={s.sidebarEmpty}>Loading...</div>}
            {!loadingGroups && myGroups.length === 0 && (
              <div style={s.sidebarEmpty}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>👥</div>
                <div>Join a group to chat</div>
                <Link to="/groups" style={s.sidebarGroupLink}>Browse Groups →</Link>
              </div>
            )}
            {myGroups.map(g => (
              <button key={g.id}
                style={{ ...s.groupBtn, ...(selectedGroup?.id === g.id ? s.groupBtnActive : {}) }}
                onClick={() => selectGroup(g)}>
                <div style={s.groupBtnIcon}>{g.icon_emoji || '⛳'}</div>
                <div style={s.groupBtnInfo}>
                  <div style={s.groupBtnName}>{g.name}</div>
                  {g.myRole === 'admin' && <div style={s.groupBtnRole}>Admin</div>}
                </div>
                {selectedGroup?.id === g.id && <div style={s.activeIndicator} />}
              </button>
            ))}
          </div>
        </div>

        {/* ── Main chat area ── */}
        <div style={s.main}>
          {selectedDM && (
            <DirectChat key={selectedDM.id} otherUser={selectedDM} session={session} myProfile={myProfile} />
          )}
          {selectedGroup && !selectedDM && (
            <GroupChat key={selectedGroup.id} group={selectedGroup} session={session}
              myProfile={myProfile} isMember={true} markSeen={markSeen} fullHeight />
          )}
          {!selectedDM && !selectedGroup && (
            <div style={s.noSelection}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0e1f13', marginBottom: 6 }}>Select a chat to get started</div>
              <div style={{ fontSize: 13, color: '#6b7c6e' }}>DMs and groups are on the left</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────
// MOBILE STYLES
// ─────────────────────────────────────────
const mob = {
  // List screen
  page: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', background: 'white',
    fontFamily: "'Barlow', sans-serif",
    paddingBottom: 72  // room for fixed Nav
  },
  header: {
    background: '#0e1f13', padding: '14px 16px 12px',
    flexShrink: 0
  },
  headerTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 24, color: '#c8f135', letterSpacing: 2
  },
  meAvatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)',
    border: '2px solid rgba(200,241,53,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', fontSize: 18
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.11)',
    borderRadius: 12, padding: '9px 12px'
  },
  searchIcon: { fontSize: 15, flexShrink: 0 },
  searchInput: {
    flex: 1, background: 'none', border: 'none', outline: 'none',
    fontSize: 15, color: 'white', fontFamily: "'Barlow', sans-serif",
    '::placeholder': { color: 'rgba(255,255,255,0.4)' }
  },
  searchClear: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
    fontSize: 14, cursor: 'pointer', padding: 0, flexShrink: 0
  },
  body: {
    flex: 1, overflowY: 'auto'
  },
  sectionLabel: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 16px 6px',
    fontSize: 11, fontWeight: 800, color: '#9aaa9d',
    letterSpacing: 1.5, textTransform: 'uppercase',
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  sectionAdd: {
    color: '#1a7a3c', fontWeight: 700, fontSize: 12,
    textDecoration: 'none', fontFamily: "'Barlow', sans-serif",
    letterSpacing: 0, textTransform: 'none'
  },
  emptySection: {
    padding: '8px 16px 12px', fontSize: 13, color: '#9aaa9d'
  },
  row: {
    width: '100%', background: 'none', border: 'none',
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 16px', cursor: 'pointer',
    textAlign: 'left', borderBottom: '1px solid #f4f7f5'
  },
  dmAvatar: {
    width: 44, height: 44, borderRadius: '50%', fontSize: 22, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden'
  },
  groupIcon: {
    width: 44, height: 44, borderRadius: 12, fontSize: 22, flexShrink: 0,
    background: '#e8f5ed',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  rowInfo: { flex: 1, minWidth: 0 },
  rowName: {
    fontWeight: 700, fontSize: 16, color: '#0e1f13',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontFamily: "'Barlow', sans-serif"
  },
  rowSub: { fontSize: 12, color: '#9aaa9d', marginTop: 1 },
  rowChevron: { fontSize: 22, color: '#c8d5ca', fontWeight: 300, flexShrink: 0 },

  // Chat screen
  chatPage: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', background: '#f4f7f5',
    fontFamily: "'Barlow', sans-serif"
  },
  chatHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px',
    background: 'white',
    borderBottom: '1px solid #f0f4f1',
    flexShrink: 0,
    paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))'
  },
  backBtn: {
    background: 'none', border: 'none',
    fontSize: 32, color: '#1a7a3c',
    cursor: 'pointer', padding: '0 4px',
    lineHeight: 1, flexShrink: 0,
    fontFamily: "'Barlow', sans-serif"
  },
  chatHeaderDMAvatar: {
    width: 38, height: 38, borderRadius: '50%', fontSize: 20, flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden'
  },
  chatHeaderGroupIcon: {
    width: 38, height: 38, borderRadius: 10, fontSize: 20, flexShrink: 0,
    background: '#e8f5ed',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  chatHeaderInfo: { flex: 1, minWidth: 0 },
  chatHeaderName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    letterSpacing: 0.3
  },
  chatHeaderSub: { fontSize: 11, color: '#9aaa9d' },
  viewProfileBtn: {
    fontSize: 11, fontWeight: 700, color: '#1a7a3c',
    textDecoration: 'none', flexShrink: 0, padding: '4px 0'
  },
  chatBody: {
    flex: 1, overflow: 'hidden', minHeight: 0,
    display: 'flex', flexDirection: 'column'
  }
}

// ─────────────────────────────────────────
// DESKTOP STYLES (unchanged)
// ─────────────────────────────────────────
const SIDEBAR_W = 240

const s = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', overflow: 'hidden',
    background: '#f4f7f5', fontFamily: "'Barlow', sans-serif"
  },
  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexShrink: 0
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 8 },
  meAvatar: {
    width: 32, height: 32, borderRadius: '50%', fontSize: 18,
    background: 'rgba(255,255,255,0.12)',
    border: '2px solid rgba(200,241,53,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
  },
  meName: { fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)' },
  body: { flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 },
  sidebar: {
    width: SIDEBAR_W, flexShrink: 0, background: '#0e1f13',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', transition: 'width 0.2s ease'
  },
  sidebarHidden: { width: 0 },
  sectionHeader: {
    padding: '12px 14px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 11, color: 'rgba(255,255,255,0.4)',
    letterSpacing: 1.5, textTransform: 'uppercase'
  },
  sidebarCreateBtn: {
    color: '#c8f135', textDecoration: 'none', fontSize: 18, fontWeight: 700,
    lineHeight: 1, padding: '2px 4px'
  },
  dmSearchWrap: { padding: '8px 10px 6px', flexShrink: 0 },
  dmSearchInput: {
    width: '100%', background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8, padding: '7px 10px',
    fontSize: 12, color: 'white', outline: 'none',
    fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  dmList: {
    overflowY: 'auto', padding: '4px 0',
    maxHeight: 220, flexShrink: 0,
    borderBottom: '1px solid rgba(255,255,255,0.07)'
  },
  dmBtn: {
    width: 'calc(100% - 16px)', background: 'none', border: 'none',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 10px', cursor: 'pointer',
    textAlign: 'left', position: 'relative',
    borderRadius: 6, margin: '1px 8px'
  },
  dmBtnActive: { background: 'rgba(200,241,53,0.12)' },
  dmBtnAvatar: {
    width: 32, height: 32, borderRadius: '50%', fontSize: 16,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    overflow: 'hidden'
  },
  dmBtnInfo: { flex: 1, minWidth: 0 },
  dmBtnName: {
    fontWeight: 700, fontSize: 12, color: 'white',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  dmBtnHandle: { fontSize: 10, color: 'rgba(255,255,255,0.4)' },
  groupList: { flex: 1, overflowY: 'auto', padding: '8px 0' },
  sidebarEmpty: {
    padding: '16px', fontSize: 11, color: 'rgba(255,255,255,0.3)',
    textAlign: 'center', lineHeight: 1.5
  },
  sidebarGroupLink: {
    display: 'block', marginTop: 8,
    color: '#c8f135', fontSize: 11, fontWeight: 700, textDecoration: 'none'
  },
  groupBtn: {
    width: 'calc(100% - 16px)', background: 'none', border: 'none',
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', cursor: 'pointer',
    textAlign: 'left', position: 'relative',
    borderRadius: 6, margin: '1px 8px'
  },
  groupBtnActive: { background: 'rgba(200,241,53,0.12)' },
  groupBtnIcon: {
    fontSize: 18, width: 34, height: 34, borderRadius: 10,
    background: 'rgba(255,255,255,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  groupBtnInfo: { flex: 1, minWidth: 0 },
  groupBtnName: {
    fontWeight: 700, fontSize: 13, color: 'white',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
  },
  groupBtnRole: { fontSize: 10, color: '#c8f135', fontWeight: 700, letterSpacing: 0.5 },
  activeIndicator: {
    position: 'absolute', left: -8, top: '50%', transform: 'translateY(-50%)',
    width: 3, height: 20, background: '#c8f135', borderRadius: 2
  },
  main: {
    flex: 1, display: 'flex', flexDirection: 'column',
    overflow: 'hidden', minWidth: 0
  },
  noSelection: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: '#6b7c6e', textAlign: 'center', padding: 40
  },
}
